import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAPI } from '../../api';
import PageSection from '../../components/PageSection';
import RawJsonDisplay from '../../components/RawJsonDisplay';
import { Role } from 'types';
import { OrgContextLink, useOrgContext, useOrgNavigate } from 'components/OrgContextProvider';
import PropEditor from 'components/PropEditor';

interface RoleContainerProps {
	onChanged: () => void;
}

const RoleContainer = ({ onChanged }: RoleContainerProps) => {
	const api = useAPI();
	const orgNavigate = useOrgNavigate();
	const ctx = useOrgContext();

	const { roleID } = useParams();

	const [role, setRole] = React.useState<Role>(
		{
			id: '',
			name: '',
			description: '',
			permissions: []
		}
	);

	const loadRole = () => {
		api.get<Role>(`${ctx?.apiBase}/roles/${roleID}`)
		.then(json => setRole(json));
	};

	useEffect(() => {
		if (roleID !== 'new') {
			loadRole();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const saveRole = () => {
		let roleData = {
			id: role.id,
			name: role.name,
			description: role.description,
			permissions: role.permissions
		};

		if (roleID === 'new') {
			api.post(`${ctx?.apiBase}/roles`, roleData)
			.then(onChanged)
			.then(() => orgNavigate('/settings/roles'));
		} else {
			api.put(`${ctx?.apiBase}/roles/${roleID}`, roleData)
			.then(onChanged)
			.then(() => orgNavigate('/settings/roles'));
		}
	};

	const isBuiltin = role.builtin === true;

	return (
		<div>
			<PageSection title={`Role: ${role?.name ?? roleID}`} level={2} />

			<PageSection borderEnd={true}>
				<OrgContextLink to={`/settings/roles`}>&#x25c0; Back to Roles Settings</OrgContextLink>
			</PageSection>

			<PageSection title={roleID === 'new' ? 'Create new role' : `${isBuiltin ? 'View' : 'Edit'} role "${role?.name ?? roleID}"`} level={4}>
				<PropEditor<Role>
					object={role}
					props={[
						{ key: 'id', label: 'ID', disabled: isBuiltin || roleID !== 'new',
							placeholder: 'Leave blank to auto-generate' },
						{ key: 'name', label: 'Name', disabled: isBuiltin || roleID !== 'new' },
						{ key: 'description', label: 'Description', disabled: isBuiltin },
						{ key: 'permissions', label: 'Permissions', disabled: isBuiltin,
							present: (value) => (value as string[]).join(', '),
							parse: (value) => value.split(',').map(s => s.trim()) },
					]}
					tools={!isBuiltin ? [{ label: 'Save Role', action: saveRole }] : []}
					onChange={setRole}
				/>
				{isBuiltin && <p><em>This is a built-in role and cannot be modified.</em></p>}
			</PageSection>

			<PageSection title="Raw Data" level={4}>
				<RawJsonDisplay data={role} />
			</PageSection>
		</div>
	);
};

export default RoleContainer;
